import React, { useEffect } from 'react'

import { useDispatch } from 'react-redux';
import { Popup } from '../../components/Popup';
import { Button } from '../../components/Button';
import { getOrderEvents } from '../../redux/orderSlice';
import { Preloader } from '../../components/Preloader';
import { formatTimestampToDayDate, getFullDateStringByStamp, getLocalUTCDate, getLocalUTCTime } from '../../helpers';

function formatChangesContentByKey(content, city) {
    const {key, prev, new:cur} = content;
    const keyNames = {
        advertType: 'Тип рекламы',
        orderType: 'Тип заявки',
        city: 'Город',
        profileType: 'Направление',
        orderStatus: 'Статус',
        worker: 'Мастер',

        sourceType: 'Источник',
        clientName: 'Имя клиента',
        orderComment: 'Коммент клиента',
        clientPhone: 'Номер клиента',
        workerComment: 'Коммент мастера',
        BSOPhotos: 'Фото бсо',
        chequePhotos: 'Фото чеков',
        'details.modern.nextWorkDate': 'Когда закончит модерн',
        'address.village': 'Адрес (поселок)',
        'address.street': 'Адрес (улица)',
        'address.apartmentNumber': 'Адрес (номер кв)',
        'address.additional': 'Адрес (доп)',

        'date.min': 'Время заявки (с)',
        'date.max': 'Время заявки (до)',
        'completionDate': 'Время завершения',
        'details.modern.completionDate': 'Когда закрыл в модерн',

        totalAmount: 'Общая',
        partsCost: 'ЗПЧ',
        clearAmount: 'Чистая',
        handOver: 'К сдаче',
        'details.modern.prepayment': 'Предоплата'
    }
    let formatKey = keyNames[key] || key;
    let formatPrev = prev;
    let formatNew = cur;

    switch(key) {
        case 'advertType':
        case 'orderType':
        case 'city':
        case 'profileType':
        case 'orderStatus': {
            formatPrev = prev?.name || '—';
            formatNew = cur?.name || '—';
            break;
        }
        case 'worker': {
            formatPrev = prev?.fullname || '—';
            formatNew = cur?.fullname || '—';
            break;
        }
        case 'BSOPhotos':
        case 'chequePhotos': {
            formatPrev = `${prev.length} фото`;
            formatNew = `${cur.length} фото`;
            break;
        }
        case 'date.min':
        case 'date.max':
        case 'completionDate':
        case 'details.modern.completionDate': {
            const d1 = prev ? getLocalUTCDate(city.UTC, prev) : null;
            const t1 = prev ? getLocalUTCTime(city.UTC, {min: prev, max: prev}, true) : null;

            const d2 = cur ? getLocalUTCDate(city.UTC, cur) : null;
            const t2 = cur ? getLocalUTCTime(city.UTC, {min: cur, max: cur}, true) : null;

            formatPrev = d1 && t1 ? `${d1} в ${t1}` : '—';
            formatNew = d2 && t2 ? `${d2} в ${t2}` : '—';
            break;
        } 
        case 'totalAmount':
        case 'partsCost':
        case 'clearAmount':
        case 'handOver':
        case 'details.modern.prepayment': {
            formatPrev = prev !== null ? prev || 0 : '—';
            formatNew = cur !== null ? cur || 0 : '—';
            break;
        }
        default: {
            formatPrev = prev ? String(prev) : '—';
            formatNew = cur ? String(cur) : '—';
        }
    }
    return {key: formatKey, prev: formatPrev, new: formatNew}
}

const OrderEventItem = ({data, city}) => {
    const {eventType, by, worker, for:_for, assignTo, accepted, changes, at} = data;

    let itemText = null;
    const timestamp = +new Date(getFullDateStringByStamp(city.UTC, at));
    const dateTime = formatTimestampToDayDate(timestamp);
    const hasChanges = !!changes?.length;
    
    //'update' | 'reset' | 'handOver' | 'return'
    switch(eventType) {
        case 'create': {
            itemText = `Заявка создана пользователем ${by.fullname}${by._id!==_for._id?` от имени пользователя ${_for.fullname}`:''} ${dateTime}`
            break;
        }
        case 'assign_now': {
            itemText = by ? `Заявка назначена мастеру ${assignTo.fullname} ${dateTime} пользователем ${by.fullname}`:'В данный момент заявка никому не назначена'
            break;
        }

        case 'assign': {
            itemText = `[${dateTime}] Заявка была назначена мастеру ${assignTo.fullname} пользователем ${by.fullname}${accepted?` (принята ${formatTimestampToDayDate(+new Date(getFullDateStringByStamp(city.UTC, accepted.at)))})`:''}`
            break;
        }
        case 'reject': {
            itemText = `[${dateTime}] Мастер ${by.fullname} не успел принять заявку`
            break;
        }
        case 'revoke': {
            itemText = `[${dateTime}] ${by.fullname} отозвал заявку`
            break;
        }

        case 'done': {
            itemText = `[${dateTime}] ${by.fullname} закрыл заявку как "Готова" ${worker ? `на мастера ${worker.fullname}`:'(без мастера)'}`
            break;
        }
        case 'modern': {
            itemText = `[${dateTime}] ${by.fullname} закрыл заявку как "Модерн" ${worker ? `на мастера ${worker.fullname}`:'(без мастера)'}`
            break;
        }
        case 'cancel': {
            itemText = `[${dateTime}] ${by.fullname} закрыл заявку как "Отмена клиентом" ${worker ? `на мастера ${worker.fullname}`:'(без мастера)'}`
            break;
        }

        case 'handOver': {
            itemText = `[${dateTime}] ${by.fullname} сдал заявку в кассу`
            break;
        }
        case 'return': {
            itemText = `[${dateTime}] ${by.fullname} вернул заявку из кассы`
            break;
        }

        case 'update': {
            itemText = `[${dateTime}] ${by.fullname} отредактировал заявку`
            break;
        }
        case 'reset': {
            itemText = `[${dateTime}] ${by.fullname} сделал откат заявки`
            break;
        }
        
        default:
    }

    const formattedChanges = changes?.map(c => formatChangesContentByKey(c, city));

    return (
        <div className="order-events__item">
            {!hasChanges ? <p>{itemText}</p> 
            : <details>
                <summary style={{listStyle:'revert'}}>{itemText}</summary>
                {formattedChanges.map((c,i) => <div key={i} className="order-events__change-row">
                    <p title={c.key}>{c.key}</p>
                    <p title={c.prev}>{c.prev}</p>
                    <p>→</p>
                    <p title={c.new}>{c.new}</p>
                </div>)}
            </details>}
        </div>
    )
}

export const OrderEventsPopup = ({closeFn, order}) => {

    const dispatch = useDispatch();

    const isLoaded = order.details.events.loadState === 'loaded';
    const eventList = order.details.events.list;

    useEffect(() => {
        dispatch(getOrderEvents(order._id));
    }, [order._id])
    const style = {width: '40%'}

    const createEvent = eventList?.length? eventList[0] : null;
    const assignEvent = eventList?.length? eventList[1] : null;
    const otherEvents = eventList ? [...eventList.slice(2)].sort((a,b) => b.at - a.at) : null;

    return (
        <Popup title={`Детали заявки id${order.id}`} closeFn={closeFn}
            button={<Button text={'Закрыть'} onClick={closeFn} />} style={style}>
            {isLoaded ? <div className='order-events'>
                <div className="order-events__header">
                    {createEvent && <OrderEventItem city={order.city} data={createEvent} />}
                    {assignEvent && <OrderEventItem city={order.city} data={assignEvent} />}
                </div>
                <div className="order-events__body">
                {otherEvents && otherEvents.map((evt, index) => <OrderEventItem city={order.city} key={index} data={evt} />)}
                </div>
            </div> : <Preloader text="Загружаю историю.." />}
        </Popup>
    )
}

// [





